"use client";

import { EA_UTILS } from "@ea/shared_types/next/ea.utils";
import dayjs, { Dayjs } from "dayjs";
import { useContext } from "react";
import { Controller, FieldError, useFormContext } from "react-hook-form";
import {
  AdapterDayjs,
  DateTimePicker,
  DateTimePickerProps,
  LocalizationProvider,
  TextFieldVariants,
} from "../materialUI";
import { EditableContext } from "./utils";

type FormDateTimePickerProps = DateTimePickerProps<Dayjs> & {
  name: string;
  dataTestid: string;
  size?: "small" | "medium";
  fullWidth?: boolean;
  label?: string;
  placeholder?: string;
  views?: string[];
  ampm?: boolean;
  variant?: TextFieldVariants;
  required?: boolean;
};

export const FormDateTimePicker = ({
  name,
  dataTestid,
  size = "small",
  fullWidth,
  label,
  placeholder,
  format = "YYYY-MM-DD HH:mm:ss",
  views = ["day", "hours", "minutes", "seconds"],
  ampm = false,
  variant = "outlined",
  required = false,
  ...props
}: FormDateTimePickerProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const fieldError = EA_UTILS.object.getNestedValue(errors, name) as FieldError | undefined;
  const editable = useContext(EditableContext);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => (
          <DateTimePicker
            ampm={ampm}
            timezone="system"
            readOnly={!editable}
            disabled={!editable}
            value={value ? dayjs(value) : null}
            views={views}
            format={format}
            timeSteps={{ minutes: 1, seconds: 1 }}
            onChange={(newValue) => {
              onChange(newValue?.toISOString());
            }}
            slotProps={{
              ...props.slotProps,
              actionBar: {
                actions: ["today", "accept", "clear"],
                sx: {
                  justifyContent: "space-between",
                  "& .MuiButton-root": {
                    color: (theme) => theme.palette.secondary.main,
                  },
                },
              },
              textField: {
                placeholder: placeholder,
                label: label,
                fullWidth: fullWidth,
                variant: variant,
                size: size,
                error: !!fieldError,
                helperText: fieldError?.message || "",
                inputProps: { "data-testid": dataTestid },
                required: required,
                ...props.slotProps?.textField,
              },
            }}
            {...props}
          />
        )}
      />
    </LocalizationProvider>
  );
};
